import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';

export const CLOUDINARY = {
  PRESET: process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_PRESET!,
  API: process.env.NEXT_PUBLIC_CLOUDINARY_API!,
  CLOUD_NAME: process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME!,
  BASE_URL: process.env.NEXT_PUBLIC_CLOUDINARY_BASE_URL!,
};

export const uploadToCloudinary = async (
  file: any,
  type: string | null = 'image'
): Promise<{ data: any; url: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', CLOUDINARY.PRESET);

  const call = await fetch(`${CLOUDINARY.API}/${type || 'image'}/upload`, {
    method: 'POST',
    body: formData,
  });
  const response = await call.json();
  if (response.error) {
    throw new Error(response.error.message);
  }
  const { asset_id, public_id, format, width, height, secure_url } = response;

  const media = {
    width,
    height,
    format,
    name: public_id,
    id: asset_id,
    secure_url,
  };

  return {
    data: media,
    url: secure_url,
  };
};

interface CloudinaryResponse {
  data: {
    width: number;
    height: number;
    format: string;
    name: string;
    id: string;
    secure_url: string;
  };
  url: string;
}

export const useUploadToCloudinary = (
  options?: UseMutationOptions<
    CloudinaryResponse,
    Error,
    {
      file: File;
      type?: string | null;
    }
  >
) =>
  useMutation({
    mutationKey: ['uploadToCloudinary'],
    mutationFn: ({ file, type = 'image' }: { file: File; type?: string | null }) =>
      uploadToCloudinary(file, type),
    ...options,
  });

export const parsePublicId = (url: string): string => url.substring(url.lastIndexOf('/') + 1);

interface CloudinaryInfoData {
  input: {
    width: number;
    height: number;
    bytes: number;
  };
  output: {
    format: string;
    bytes: number;
    width: number;
    height: number;
  };
}

export const getCloudinaryImageInfo = async (
  publicId: string
): Promise<CloudinaryInfoData | null> => {
  try {
    const call = await fetch(`${CLOUDINARY.BASE_URL}/image/upload/fl_getinfo/${publicId}`);
    return await call.json();
  } catch {
    return null;
  }
};

export const getCloudinaryImageBySettings = (publicId: string, settings: string): string =>
  `${CLOUDINARY.BASE_URL}/image/upload/${settings}/${publicId}`;
